import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSuperAdminPlans,
  clearSuperAdminPlanGatewayRefs,
  bulkDeactivateSuperAdminPlans,
  SaPlan,
} from "../../../helpers/super_admin_plans_helper";
import { formatCents, intlLocaleFromI18n } from "../../../helpers/money";
import PlanFormModal from "./PlanFormModal";
import PlanVersionsModal from "./PlanVersionsModal";

const PER_PAGE = 50;

type VisibilityFilter = "" | "public" | "private" | "internal";
type ActiveFilter = "" | "1" | "0";

/**
 * Listagem + CRUD de planos SA (LAC-014 · DOC 02 § 2.4–2.5).
 * Bulk + modal force (LAC-195) + timeline versões (LAC-063) + polish anti-viés.
 */
const SuperAdminPlansList = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<VisibilityFilter>("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("1");
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SaPlan | null>(null);
  const [versionsPlan, setVersionsPlan] = useState<SaPlan | null>(null);
  const [clearingId, setClearingId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);
  /** null = fechado; confirm = sem assinantes; force = SA confirma com assinantes. */
  const [bulkModal, setBulkModal] = useState<"confirm" | "force" | null>(null);
  /** IDs que falharam sem force (fallback se a contagem da lista estiver stale). */
  const [bulkForceIds, setBulkForceIds] = useState<number[]>([]);
  /** Desativar uma linha: override da seleção do checkbox. */
  const [bulkOverrideIds, setBulkOverrideIds] = useState<number[] | null>(null);
  /** No force: também desativar planos da seleção sem assinantes (default sim). */
  const [forceIncludeSafe, setForceIncludeSafe] = useState(true);
  const [inactiveTrial, setInactiveTrial] = useState<SaPlan | null>(null);

  const showInitialLoading = loading && plans.length === 0 && !error;
  const showRefreshOverlay = loading && plans.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSuperAdminPlans({
        per_page: PER_PAGE,
        page,
        currency: "BRL",
        visibility: visibility || undefined,
        is_active:
          activeFilter === "" ? undefined : activeFilter === "1",
      });
      if (!res?.success) {
        setError(t("sa.plans.error.load"));
        setPlans([]);
        setTotal(0);
        setLastPage(1);
        return;
      }
      setPlans(res.data || []);
      setTotal(res.meta?.total ?? (res.data || []).length);
      setLastPage(res.meta?.last_page ?? 1);
      setSelectedIds({});

      try {
        const probe = await getSuperAdminPlans({
          per_page: 50,
          page: 1,
          currency: "BRL",
        });
        const trial = (probe.data || []).find((p) => p.is_trial_plan);
        setInactiveTrial(trial && !trial.is_active ? trial : null);
      } catch {
        setInactiveTrial(null);
      }
    } catch (err: any) {
      setError(err?.message || t("sa.plans.error.load"));
      setPlans([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setLoading(false);
    }
  }, [t, page, visibility, activeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  document.title = t("sa.plans.documentTitle");

  const onVisibilityChange = (value: string) => {
    setPage(1);
    setVisibility(value as VisibilityFilter);
  };

  const onActiveChange = (value: string) => {
    setPage(1);
    setActiveFilter(value as ActiveFilter);
  };

  const toggleDescription = (id: number) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openCreate = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const openEdit = (plan: SaPlan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const openVersions = (plan: SaPlan) => {
    setVersionsPlan(plan);
  };

  const clearGatewayRefs = async (plan: SaPlan) => {
    const hasRef = !!(plan.gateway_plan_ref || plan.mp_preapproval_plan_id);
    if (
      !window.confirm(
        t(
          hasRef
            ? "sa.plans.clearGateway.confirm"
            : "sa.plans.clearGateway.confirmEmpty"
        )
      )
    ) {
      return;
    }
    setClearingId(plan.id);
    try {
      const res = await clearSuperAdminPlanGatewayRefs(plan.id);
      if (!res?.success) {
        toast.error(t("sa.plans.clearGateway.error"));
        return;
      }
      toast.success(t("sa.plans.clearGateway.success"));
      await load();
    } catch {
      toast.error(t("sa.plans.clearGateway.error"));
    } finally {
      setClearingId(null);
    }
  };

  const selectedActivePlans = plans.filter(
    (p) => selectedIds[p.id] && p.is_active
  );
  const selectedActiveIds = selectedActivePlans.map((p) => p.id);

  /** Planos efetivos desta operação (bulk ou uma linha). */
  const workingPlans = useMemo(() => {
    if (bulkOverrideIds && bulkOverrideIds.length > 0) {
      return plans.filter(
        (p) => bulkOverrideIds.includes(p.id) && p.is_active
      );
    }
    return selectedActivePlans;
  }, [bulkOverrideIds, plans, selectedActivePlans]);

  const workingIds = workingPlans.map((p) => p.id);

  const workingWithSubscribers = workingPlans.filter(
    (p) => (p.subscriptions_count ?? 0) > 0
  );

  const workingSafeCount = workingPlans.length - workingWithSubscribers.length;

  const toggleSelect = (id: number, checked: boolean) => {
    setSelectedIds((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleSelectAllActive = (checked: boolean) => {
    const next: Record<number, boolean> = {};
    plans.forEach((p) => {
      if (p.is_active) {
        next[p.id] = checked;
      }
    });
    setSelectedIds(next);
  };

  /** Só planos ativos que já têm assinantes — acelera o force consciente. */
  const selectWithSubscribersOnly = () => {
    const next: Record<number, boolean> = {};
    let n = 0;
    plans.forEach((p) => {
      if (p.is_active && (p.subscriptions_count ?? 0) > 0) {
        next[p.id] = true;
        n += 1;
      }
    });
    setSelectedIds(next);
    if (n === 0) {
      toast.info(t("sa.plans.bulkDeactivate.selectWithSubsNone"));
    }
  };

  const closeBulkModal = () => {
    if (bulkBusy) {
      return;
    }
    setBulkModal(null);
    setBulkForceIds([]);
    setBulkOverrideIds(null);
    setForceIncludeSafe(true);
  };

  const openBulkDeactivate = () => {
    if (selectedActiveIds.length === 0) {
      toast.info(t("sa.plans.bulkDeactivate.none"));
      return;
    }
    setBulkOverrideIds(null);
    setBulkForceIds([]);
    setForceIncludeSafe(true);
    setBulkModal(
      selectedActivePlans.some((p) => (p.subscriptions_count ?? 0) > 0)
        ? "force"
        : "confirm"
    );
  };

  /** Desativar um plano na linha — mesmo modal do bulk. */
  const openRowDeactivate = (plan: SaPlan) => {
    if (!plan.is_active) {
      return;
    }
    setBulkOverrideIds([plan.id]);
    setBulkForceIds([]);
    setForceIncludeSafe(true);
    setBulkModal(
      (plan.subscriptions_count ?? 0) > 0 ? "force" : "confirm"
    );
  };

  const reportBulkResult = (
    deactivated: number[],
    skipped: number[],
    remainingErrors: number
  ) => {
    if (deactivated.length > 0) {
      toast.success(
        t("sa.plans.bulkDeactivate.success", {
          count: deactivated.length,
        })
      );
    }
    if (skipped.length > 0) {
      toast.info(
        t("sa.plans.bulkDeactivate.skipped", {
          count: skipped.length,
        })
      );
    }
    if (remainingErrors > 0) {
      toast.warning(t("sa.plans.bulkDeactivate.partialErrors"));
    }
    if (
      deactivated.length === 0 &&
      skipped.length === 0 &&
      remainingErrors === 0
    ) {
      toast.info(t("sa.plans.bulkDeactivate.success", { count: 0 }));
    }
  };

  const runBulkDeactivate = async (force: boolean, planIds: number[]) => {
    if (planIds.length === 0) {
      toast.info(t("sa.plans.bulkDeactivate.none"));
      return;
    }
    setBulkBusy(true);
    try {
      const res = await bulkDeactivateSuperAdminPlans(planIds, force);
      if (!res?.success) {
        toast.error(t("sa.plans.bulkDeactivate.error"));
        return;
      }
      const deactivated = res.data?.deactivated ?? [];
      const skipped = res.data?.skipped ?? [];
      const errors = res.data?.errors ?? {};
      const blockedIds = Object.keys(errors)
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (!force && blockedIds.length > 0) {
        reportBulkResult(deactivated, skipped, 0);
        setBulkForceIds(blockedIds);
        setBulkOverrideIds(null);
        setForceIncludeSafe(false);
        setBulkModal("force");
        await load();
        return;
      }

      reportBulkResult(
        deactivated,
        skipped,
        force ? blockedIds.length : 0
      );
      setBulkModal(null);
      setBulkForceIds([]);
      setBulkOverrideIds(null);
      await load();
    } catch {
      toast.error(t("sa.plans.bulkDeactivate.error"));
    } finally {
      setBulkBusy(false);
    }
  };

  const forceModalPlans: SaPlan[] =
    bulkForceIds.length > 0
      ? plans.filter((p) => bulkForceIds.includes(p.id))
      : workingWithSubscribers;

  const safeWorkingIds = workingPlans
    .filter((p) => (p.subscriptions_count ?? 0) === 0)
    .map((p) => p.id);

  const forceTargetIds = (() => {
    if (bulkForceIds.length > 0) {
      return bulkForceIds;
    }
    const blocked = forceModalPlans.map((p) => p.id);
    if (forceIncludeSafe && safeWorkingIds.length > 0) {
      return Array.from(new Set([...blocked, ...safeWorkingIds]));
    }
    return blocked.length > 0 ? blocked : workingIds;
  })();

  const totalForceSubscribers = forceModalPlans.reduce(
    (sum, p) => sum + (p.subscriptions_count ?? 0),
    0
  );

  /** Impacto no catálogo público (site/checkout) — “dry-run” textual. */
  const impactIds =
    bulkModal === "force" ? forceTargetIds : workingIds;
  const impactPlans = plans.filter((p) => impactIds.includes(p.id));
  const publicLeaving = impactPlans.filter((p) => p.visibility === "public");
  const trialInImpact = impactPlans.some((p) => p.is_trial_plan);

  const hasFilters = visibility !== "" || activeFilter !== "";

  const visibilityBadge = (vis: string) => {
    const color =
      vis === "public" ? "success" : vis === "private" ? "warning" : "secondary";
    return (
      <Badge color={color} className="text-uppercase">
        {t(`sa.plans.visibility.${vis}`, { defaultValue: vis })}
      </Badge>
    );
  };

  const statusBadge = (active: boolean) => (
    <Badge color={active ? "success" : "secondary"}>
      {active ? t("sa.plans.status.active") : t("sa.plans.status.inactive")}
    </Badge>
  );

  const formatQuota = (quota: number) =>
    t("sa.plans.quotaValue", {
      count: quota,
      formatted: new Intl.NumberFormat(
        intlLocaleFromI18n(i18n.language)
      ).format(quota),
    });

  const formatSubscribers = (count?: number) =>
    new Intl.NumberFormat(intlLocaleFromI18n(i18n.language)).format(
      count ?? 0
    );

  const catalogImpactBlock =
    publicLeaving.length > 0 || trialInImpact ? (
      <Alert color="info" className="mt-3 mb-0">
        {publicLeaving.length > 0 ? (
          <p className="mb-1">
            {t("sa.plans.bulkDeactivate.catalogImpact", {
              count: publicLeaving.length,
              names: publicLeaving.map((p) => p.name).join(", "),
            })}
          </p>
        ) : null}
        {trialInImpact ? (
          <p className="mb-0 fw-medium">
            {t("sa.plans.bulkDeactivate.trialWarning")}
          </p>
        ) : null}
      </Alert>
    ) : null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.plans.title")}
          pageTitle={t("sa.menu.plans")}
        />
        <ToastContainer closeButton={false} limit={3} />

        {error ? (
          <Alert
            color="danger"
            className="d-flex align-items-center justify-content-between gap-2"
          >
            <span>{error}</span>
            <Button
              color="light"
              size="sm"
              onClick={() => load()}
              disabled={loading}
            >
              {t("sa.plans.retry")}
            </Button>
          </Alert>
        ) : null}

        {inactiveTrial ? (
          <Alert
            color="warning"
            className="d-flex flex-wrap align-items-center justify-content-between gap-2"
          >
            <span>{t("sa.plans.trialOff.banner")}</span>
            <Button
              color="warning"
              size="sm"
              outline
              onClick={() => openEdit(inactiveTrial)}
            >
              {t("sa.plans.trialOff.bannerCta")}
            </Button>
          </Alert>
        ) : null}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <h5 className="card-title mb-0">{t("sa.plans.tableTitle")}</h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {!loading && !error ? (
                      <span className="text-muted small">
                        {t("sa.plans.totalCount", { count: total })}
                      </span>
                    ) : null}
                    <Button
                      color="primary"
                      size="sm"
                      onClick={openCreate}
                      disabled={loading}
                    >
                      <i className="ri-add-line align-bottom me-1" aria-hidden />
                      {t("sa.plans.create")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      onClick={selectWithSubscribersOnly}
                      disabled={loading || bulkBusy}
                      title={t("sa.plans.bulkDeactivate.selectWithSubsTitle")}
                    >
                      {t("sa.plans.bulkDeactivate.selectWithSubs")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      onClick={openBulkDeactivate}
                      disabled={
                        loading || bulkBusy || selectedActiveIds.length === 0
                      }
                    >
                      {bulkBusy ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      {t("sa.plans.bulkDeactivate.cta")}
                      {selectedActiveIds.length > 0
                        ? ` (${selectedActiveIds.length})`
                        : ""}
                    </Button>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 200 }}
                    value={visibility}
                    onChange={(e) => onVisibilityChange(e.target.value)}
                    aria-label={t("sa.plans.filter.visibility")}
                    disabled={loading}
                  >
                    <option value="">{t("sa.plans.filter.visibilityAll")}</option>
                    <option value="public">
                      {t("sa.plans.visibility.public")}
                    </option>
                    <option value="private">
                      {t("sa.plans.visibility.private")}
                    </option>
                    <option value="internal">
                      {t("sa.plans.visibility.internal")}
                    </option>
                  </Input>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 200 }}
                    value={activeFilter}
                    onChange={(e) => onActiveChange(e.target.value)}
                    aria-label={t("sa.plans.filter.status")}
                    disabled={loading}
                  >
                    <option value="">{t("sa.plans.filter.statusAll")}</option>
                    <option value="1">{t("sa.plans.status.active")}</option>
                    <option value="0">{t("sa.plans.status.inactive")}</option>
                  </Input>
                </div>
              </CardHeader>
              <CardBody className="position-relative">
                {showRefreshOverlay ? (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                    style={{ zIndex: 2 }}
                    role="status"
                    aria-live="polite"
                  >
                    <Spinner color="primary" className="me-2" />
                    <span className="text-muted">{t("sa.plans.loading")}</span>
                  </div>
                ) : null}

                {showInitialLoading ? (
                  <div className="text-center py-5" role="status">
                    <Spinner color="primary" className="me-2" />
                    <span className="text-muted">{t("sa.plans.loading")}</span>
                  </div>
                ) : plans.length === 0 && !error ? (
                  <div className="text-center py-5 px-3">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                        <i
                          className="ri-price-tag-3-line"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h5 className="mb-2">
                      {hasFilters
                        ? t("sa.plans.empty.filteredTitle")
                        : t("sa.plans.empty.title")}
                    </h5>
                    <p
                      className="text-muted mb-3 mx-auto"
                      style={{ maxWidth: 420 }}
                    >
                      {hasFilters
                        ? t("sa.plans.empty.filteredDescription")
                        : t("sa.plans.empty.description")}
                    </p>
                    {hasFilters ? (
                      <Button
                        color="light"
                        onClick={() => {
                          setVisibility("");
                          setActiveFilter("");
                          setPage(1);
                        }}
                      >
                        {t("sa.plans.filter.clear")}
                      </Button>
                    ) : (
                      <Button color="primary" onClick={openCreate}>
                        {t("sa.plans.create")}
                      </Button>
                    )}
                  </div>
                ) : plans.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" style={{ width: 36 }}>
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                aria-label={t("sa.plans.selectAll")}
                                checked={
                                  plans.some((p) => p.is_active) &&
                                  plans
                                    .filter((p) => p.is_active)
                                    .every((p) => selectedIds[p.id])
                                }
                                onChange={(e) =>
                                  toggleSelectAllActive(e.target.checked)
                                }
                                disabled={loading || bulkBusy}
                              />
                            </th>
                            <th scope="col">{t("sa.plans.table.name")}</th>
                            <th scope="col">
                              {t("sa.plans.table.visibility")}
                            </th>
                            <th scope="col">{t("sa.plans.table.price")}</th>
                            <th scope="col">{t("sa.plans.table.interval")}</th>
                            <th scope="col">{t("sa.plans.table.quota")}</th>
                            <th scope="col">
                              {t("sa.plans.table.subscribers")}
                            </th>
                            <th scope="col">{t("sa.plans.table.status")}</th>
                            <th scope="col" className="text-end">
                              {t("sa.plans.table.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {plans.map((plan) => {
                            const expanded = !!expandedIds[plan.id];
                            const hasDescription = !!(
                              plan.description && plan.description.trim()
                            );
                            return (
                              <React.Fragment key={plan.id}>
                                <tr>
                                  <td>
                                    {plan.is_active ? (
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={!!selectedIds[plan.id]}
                                        onChange={(e) =>
                                          toggleSelect(
                                            plan.id,
                                            e.target.checked
                                          )
                                        }
                                        disabled={loading || bulkBusy}
                                        aria-label={plan.name}
                                      />
                                    ) : null}
                                  </td>
                                  <td
                                    className="text-wrap"
                                    style={{ minWidth: 180 }}
                                  >
                                    <div className="fw-medium d-flex flex-wrap align-items-center gap-1">
                                      <span>{plan.name}</span>
                                      {(plan.subscriptions_count ?? 0) > 0 ? (
                                        <Badge
                                          color="soft-warning"
                                          className="text-warning"
                                          title={t(
                                            "sa.plans.badge.subscribersTitle",
                                            {
                                              count:
                                                plan.subscriptions_count ?? 0,
                                            }
                                          )}
                                        >
                                          {t("sa.plans.badge.subscribers", {
                                            count:
                                              plan.subscriptions_count ?? 0,
                                          })}
                                        </Badge>
                                      ) : null}
                                      {(plan.versions_count ?? 0) > 1 ? (
                                        <Badge
                                          color="soft-info"
                                          className="text-info"
                                          role="button"
                                          title={t(
                                            "sa.plans.versions.badgeTitle"
                                          )}
                                          onClick={() => openVersions(plan)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          {t("sa.plans.versions.badge", {
                                            count: plan.versions_count ?? 0,
                                          })}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    <div className="text-muted small">
                                      {plan.slug}
                                    </div>
                                    {plan.is_trial_plan ? (
                                      <Badge color="info" className="mt-1">
                                        {t("sa.plans.badge.trial")}
                                      </Badge>
                                    ) : null}
                                    {hasDescription ? (
                                      <div className="mt-1">
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0"
                                          onClick={() =>
                                            toggleDescription(plan.id)
                                          }
                                          aria-expanded={expanded}
                                        >
                                          {expanded
                                            ? t("sa.plans.description.hide")
                                            : t("sa.plans.description.show")}
                                        </button>
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>{visibilityBadge(plan.visibility)}</td>
                                  <td className="text-nowrap">
                                    {formatCents(
                                      plan.base_price_cents,
                                      plan.currency || "BRL",
                                      intlLocaleFromI18n(i18n.language)
                                    )}
                                  </td>
                                  <td className="text-nowrap">
                                    {t(
                                      `sa.plans.interval.${plan.billing_interval}`,
                                      {
                                        defaultValue: plan.billing_interval,
                                      }
                                    )}
                                  </td>
                                  <td className="text-nowrap">
                                    {formatQuota(plan.email_quota)}
                                  </td>
                                  <td className="text-nowrap">
                                    {(plan.subscriptions_count ?? 0) > 0 ? (
                                      <Link
                                        to={`/super-admin/tenants?plan_id=${plan.id}`}
                                        className="link-primary"
                                        title={t(
                                          "sa.plans.bulkDeactivate.viewTenantsTitle"
                                        )}
                                      >
                                        {formatSubscribers(
                                          plan.subscriptions_count
                                        )}
                                      </Link>
                                    ) : (
                                      formatSubscribers(
                                        plan.subscriptions_count
                                      )
                                    )}
                                  </td>
                                  <td>{statusBadge(plan.is_active)}</td>
                                  <td className="text-end text-nowrap">
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="me-1"
                                      onClick={() => openEdit(plan)}
                                      disabled={
                                        loading || clearingId === plan.id
                                      }
                                    >
                                      {t("sa.plans.edit")}
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="me-1"
                                      onClick={() => openVersions(plan)}
                                      disabled={loading}
                                      title={t("sa.plans.versions.ctaTitle")}
                                    >
                                      {t("sa.plans.versions.cta")}
                                    </Button>
                                    {plan.is_active ? (
                                      <Button
                                        color="light"
                                        size="sm"
                                        className="me-1"
                                        onClick={() => openRowDeactivate(plan)}
                                        disabled={loading || bulkBusy}
                                        title={t(
                                          "sa.plans.bulkDeactivate.rowTitle"
                                        )}
                                      >
                                        {t("sa.plans.bulkDeactivate.rowCta")}
                                      </Button>
                                    ) : null}
                                    <Button
                                      color="light"
                                      size="sm"
                                      title={t("sa.plans.clearGateway.title")}
                                      onClick={() => clearGatewayRefs(plan)}
                                      disabled={
                                        loading || clearingId === plan.id
                                      }
                                    >
                                      {clearingId === plan.id
                                        ? t("sa.plans.clearGateway.working")
                                        : t("sa.plans.clearGateway.cta")}
                                    </Button>
                                  </td>
                                </tr>
                                {expanded && hasDescription ? (
                                  <tr className="bg-light">
                                    <td colSpan={9} className="text-wrap">
                                      <p className="text-muted mb-0 small">
                                        {plan.description}
                                      </p>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 ? (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("sa.plans.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("sa.plans.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage || loading}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("sa.plans.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <PlanFormModal
        isOpen={modalOpen}
        plan={editingPlan}
        onClose={() => {
          setModalOpen(false);
          setEditingPlan(null);
        }}
        onSaved={() => load()}
      />

      <PlanVersionsModal
        isOpen={versionsPlan !== null}
        plan={versionsPlan}
        onClose={() => setVersionsPlan(null)}
        onEdit={(row) => {
          setVersionsPlan(null);
          openEdit(row);
        }}
      />

      <Modal isOpen={bulkModal !== null} toggle={closeBulkModal} centered>
        <ModalHeader toggle={closeBulkModal}>
          {bulkModal === "force"
            ? t("sa.plans.bulkDeactivate.forceTitle")
            : t("sa.plans.bulkDeactivate.confirmTitle")}
        </ModalHeader>
        <ModalBody>
          {bulkModal === "confirm" ? (
            <>
              <p className="mb-0">
                {t("sa.plans.bulkDeactivate.confirm", {
                  count: workingIds.length,
                })}
              </p>
              {catalogImpactBlock}
            </>
          ) : null}
          {bulkModal === "force" ? (
            <>
              <Alert color="warning" className="mb-3">
                {t("sa.plans.bulkDeactivate.forceIntro", {
                  plans: forceModalPlans.length || forceTargetIds.length,
                  subscribers: totalForceSubscribers,
                })}
              </Alert>
              {forceModalPlans.length > 0 ? (
                <ul className="mb-3 ps-3">
                  {forceModalPlans.map((p) => (
                    <li key={p.id} className="mb-1">
                      <span className="fw-medium">{p.name}</span>
                      <span className="text-muted">
                        {" "}
                        —{" "}
                        {t("sa.plans.bulkDeactivate.forcePlanLine", {
                          count: p.subscriptions_count ?? 0,
                        })}
                      </span>
                      {(p.subscriptions_count ?? 0) > 0 ? (
                        <>
                          {" "}
                          <Link
                            to={`/super-admin/tenants?plan_id=${p.id}`}
                            className="small"
                            onClick={closeBulkModal}
                          >
                            {t("sa.plans.bulkDeactivate.viewTenants")}
                          </Link>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="text-muted small mb-2">
                {t("sa.plans.bulkDeactivate.forceHint")}
              </p>
              {bulkForceIds.length === 0 && workingSafeCount > 0 ? (
                <FormGroup check className="mb-2">
                  <Input
                    type="checkbox"
                    id="forceIncludeSafe"
                    checked={forceIncludeSafe}
                    onChange={(e) => setForceIncludeSafe(e.target.checked)}
                    disabled={bulkBusy}
                  />
                  <Label check htmlFor="forceIncludeSafe" className="ms-1">
                    {t("sa.plans.bulkDeactivate.forceAlsoSafeCheck", {
                      count: workingSafeCount,
                    })}
                  </Label>
                </FormGroup>
              ) : null}
              {catalogImpactBlock}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeBulkModal} disabled={bulkBusy}>
            {t("sa.plans.bulkDeactivate.cancel")}
          </Button>
          {bulkModal === "confirm" ? (
            <Button
              color="primary"
              disabled={bulkBusy}
              onClick={() => void runBulkDeactivate(false, workingIds)}
            >
              {bulkBusy ? <Spinner size="sm" className="me-1" /> : null}
              {t("sa.plans.bulkDeactivate.confirmButton")}
            </Button>
          ) : null}
          {bulkModal === "force" ? (
            <Button
              color="warning"
              disabled={bulkBusy || forceTargetIds.length === 0}
              onClick={() => void runBulkDeactivate(true, forceTargetIds)}
            >
              {bulkBusy ? <Spinner size="sm" className="me-1" /> : null}
              {t("sa.plans.bulkDeactivate.forceButton")}
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SuperAdminPlansList;
